import jsPDF from "jspdf";

import invoiceTemplate from "@/assets/invoice-template.png";
import { DEFAULT_INVOICE_TEMPLATE_LAYOUT } from "@/utils/invoiceTemplateLayout";

interface InvoiceItem {
  serviceId: string;
  name: string;
  description: string | null;
  unitPrice: number | string;
  quantity: number | string;
  total: number | string;
  type: string;
}

interface InvoiceData {
  invoice_number: string;
  invoice_date: string;
  amount: number | string;
  signature_url: string | null;
  patient: {
    name: string;
    email?: string | null;
    phone?: string | null;
    address?: string | null;
    date_of_birth?: string | null;
    first_name?: string | null;
    last_name?: string | null;
  };
  service?: {
    name: string;
    description: string | null;
    price: number | string;
  };
  items?: InvoiceItem[];
  practitioner: {
    business_name: string;
    practitioner_name: string;
    address: string;
    phone: string;
    email: string;
    siret: string | null;
  };
}

const num = (v: unknown, fallback = 0) => {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
};

// Split full name into prénom / nom (first word = prénom, rest = nom)
const splitName = (full: string): { prenom: string; nom: string } => {
  const parts = (full || "").trim().split(/\s+/);
  if (parts.length === 0) return { prenom: "", nom: "" };
  if (parts.length === 1) return { prenom: parts[0], nom: "" };
  return { prenom: parts[0], nom: parts.slice(1).join(" ") };
};

const formatDateFR = (iso?: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const TEXT_RGB = { r: 60, g: 60, b: 60 };
const TEMPLATE_WIDTH_PX = 1023;
const TEMPLATE_HEIGHT_PX = 1537;

export const generateInvoicePDF = async (
  invoice: InvoiceData,
  options: { save?: boolean } = {}
): Promise<Uint8Array> => {
  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  const pageW = 210;
  const pageH = 297;
  const pxToMmX = (px: number) => (px / TEMPLATE_WIDTH_PX) * pageW;
  const pxToMmY = (px: number) => (px / TEMPLATE_HEIGHT_PX) * pageH;
  const layout = DEFAULT_INVOICE_TEMPLATE_LAYOUT;

  // === Background template (full page) ===
  try {
    doc.addImage(invoiceTemplate, "PNG", 0, 0, pageW, pageH, undefined, "FAST");
  } catch (e) {
    console.warn("Template facture non chargé:", e);
  }

  // Default font / color for overlays
  doc.setTextColor(TEXT_RGB.r, TEXT_RGB.g, TEXT_RGB.b);
  doc.setFont("helvetica", "normal");

  const write = (
    text: string,
    x: number,
    y: number,
    opts: {
      size?: number;
      bold?: boolean;
      align?: "left" | "center" | "right";
      baseline?: "alphabetic" | "top" | "middle" | "bottom" | "hanging" | "ideographic";
      color?: { r: number; g: number; b: number };
    } = {}
  ) => {
    const { size = 10, bold = false, align = "left", baseline = "alphabetic", color = TEXT_RGB } = opts;
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(size);
    doc.setTextColor(color.r, color.g, color.b);
    doc.text(text || "", x, y, { align, baseline });
  };

  // === HEADER: N° facture + Date d'émission ===
  write(invoice.invoice_number || "", pxToMmX(layout.invoiceNumber.x), pxToMmY(layout.invoiceNumber.y), {
    size: layout.invoiceNumber.fontSize,
    bold: layout.invoiceNumber.bold,
    align: layout.invoiceNumber.align,
    baseline: "top",
  });
  write(formatDateFR(invoice.invoice_date), pxToMmX(layout.invoiceDate.x), pxToMmY(layout.invoiceDate.y), {
    size: layout.invoiceDate.fontSize,
    bold: layout.invoiceDate.bold,
    align: layout.invoiceDate.align,
    baseline: "top",
  });

  // === CONSULTANT BLOC ===
  const { prenom, nom } = (() => {
    if (invoice.patient.first_name || invoice.patient.last_name) {
      return {
        prenom: invoice.patient.first_name || "",
        nom: invoice.patient.last_name || "",
      };
    }
    return splitName(invoice.patient.name || "");
  })();

  const consultationDate = formatDateFR(invoice.invoice_date);
  const firstItem = (invoice.items && invoice.items[0]) || null;
  const consultationType = firstItem ? firstItem.name : invoice.service?.name || "";

  write(nom.toUpperCase(), pxToMmX(layout.patientLastName.x), pxToMmY(layout.patientLastName.y), {
    size: layout.patientLastName.fontSize,
    bold: layout.patientLastName.bold,
    align: layout.patientLastName.align,
    baseline: "top",
  });
  write(prenom, pxToMmX(layout.patientFirstName.x), pxToMmY(layout.patientFirstName.y), {
    size: layout.patientFirstName.fontSize,
    bold: layout.patientFirstName.bold,
    align: layout.patientFirstName.align,
    baseline: "top",
  });
  write(formatDateFR(invoice.patient.date_of_birth), pxToMmX(layout.patientBirthDate.x), pxToMmY(layout.patientBirthDate.y), {
    size: layout.patientBirthDate.fontSize,
    bold: layout.patientBirthDate.bold,
    align: layout.patientBirthDate.align,
    baseline: "top",
  });
  write(consultationDate, pxToMmX(layout.consultationDate.x), pxToMmY(layout.consultationDate.y), {
    size: layout.consultationDate.fontSize,
    bold: layout.consultationDate.bold,
    align: layout.consultationDate.align,
    baseline: "top",
  });

  const typeLines = doc.splitTextToSize(consultationType || "", pxToMmX(layout.consultationType.width || 220));
  typeLines.slice(0, 2).forEach((l: string, i: number) => {
    write(l, pxToMmX(layout.consultationType.x), pxToMmY(layout.consultationType.y + i * (layout.consultationType.lineHeight || 24)), {
      size: layout.consultationType.fontSize,
      bold: layout.consultationType.bold,
      align: layout.consultationType.align,
      baseline: "top",
    });
  });

  // === ITEMS TABLE ===
  // Table body roughly y=170 to y=183mm
  const items: InvoiceItem[] =
    invoice.items && invoice.items.length > 0
      ? invoice.items
      : invoice.service
        ? [
            {
              serviceId: "",
              name: invoice.service.name,
              description: invoice.service.description,
              unitPrice: invoice.service.price,
              quantity: 1,
              total: invoice.service.price,
              type: "service",
            },
          ]
        : [];

  // Column positions calibrated on the template image itself
  const COL_DESIGNATION_X = pxToMmX(255);
  const COL_QTE_X = pxToMmX(624);
  const COL_PU_X = pxToMmX(764);
  const COL_TOTAL_X = pxToMmX(925);
  const designationWidth = pxToMmX(500) - pxToMmX(37);
  const tableStartY = pxToMmY(880);
  const rowH = 7.8;
  const maxRows = 5;

  const displayed = items.slice(0, maxRows);
  displayed.forEach((item, i) => {
    const y = tableStartY + i * rowH;
    const designation = doc.splitTextToSize(item.name || "", designationWidth).slice(0, 2);
    designation.forEach((line: string, lineIndex: number) => {
      write(line, COL_DESIGNATION_X, y + lineIndex * 3.2, { size: 10, align: "center", baseline: "top" });
    });
    write(num(item.quantity, 1).toString(), COL_QTE_X, y, { size: 10, align: "center", baseline: "top" });
    write(`${num(item.unitPrice).toFixed(2)} €`, COL_PU_X, y, { size: 10, align: "center", baseline: "top" });
    write(`${num(item.total).toFixed(2)} €`, COL_TOTAL_X, y, { size: 10, align: "center", baseline: "top" });
  });
  if (items.length > maxRows) {
    write(`+ ${items.length - maxRows} autre(s) article(s)`, COL_DESIGNATION_X, tableStartY + maxRows * rowH, {
      size: 8,
      align: "center",
    });
  }

  // === TOTALS ===
  const computedTotal = items.reduce((s, it) => s + num(it.total), 0);
  const totalAmount = num(invoice.amount) > 0 ? num(invoice.amount) : computedTotal;

  // Sous-total HT value
  write(totalAmount.toFixed(2), pxToMmX(layout.subtotal.x), pxToMmY(layout.subtotal.y), {
    size: layout.subtotal.fontSize,
    bold: layout.subtotal.bold,
    align: layout.subtotal.align,
    baseline: "top",
  });
  // TOTAL TTC value
  write(totalAmount.toFixed(2), pxToMmX(layout.total.x), pxToMmY(layout.total.y), {
    size: layout.total.fontSize,
    bold: layout.total.bold,
    align: layout.total.align,
    baseline: "top",
  });

  // === Save / return ===
  const fileName = `Facture_${invoice.invoice_number}_${(invoice.patient?.name || "client").replace(
    /\s+/g,
    "_"
  )}.pdf`;
  const buffer = doc.output("arraybuffer") as ArrayBuffer;
  const pdfBytes = new Uint8Array(buffer);
  if (options.save !== false) {
    doc.save(fileName);
  }
  return pdfBytes;
};
